import React, { useState, useEffect } from "react";
import {
  FileText,
  Plus,
  Edit2,
  Trash2,
  Search,
  X,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Upload,
  Download,
  Eye,
  FolderOpen,
  File,
  FileImage,
  FileSpreadsheet,
  Filter,
} from "lucide-react";
import { supabase } from "../lib/supabase";

// Import conditionnel du logger (ne bloque pas si absent)
let logCreate, logUpdate, logDelete, AUDIT_TYPES;
try {
  const auditLogger = require("../lib/auditLogger");
  logCreate = auditLogger.logCreate;
  logUpdate = auditLogger.logUpdate;
  logDelete = auditLogger.logDelete;
  AUDIT_TYPES = auditLogger.AUDIT_TYPES;
} catch (e) {
  console.warn("Audit logger not available");
  logCreate = async () => {};
  logUpdate = async () => {};
  logDelete = async () => {};
  AUDIT_TYPES = { DOCUMENT: "document" };
}

// ============================================
// FONCTIONS SUPABASE
// ============================================

const loadDocumentsFromDB = async (entrepriseId) => {
  const { data, error } = await supabase
    .from("documents")
    .select("*")
    .eq("entreprise_id", entrepriseId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
};

const addDocumentToDB = async (documentData) => {
  const { data, error } = await supabase
    .from("documents")
    .insert([documentData])
    .select()
    .single();

  if (error) throw error;
  return data;
};

const updateDocumentInDB = async (id, updates) => {
  const { data, error } = await supabase
    .from("documents")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

const deleteDocumentFromDB = async (id) => {
  const { error } = await supabase.from("documents").delete().eq("id", id);

  if (error) throw error;
  return true;
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const DocumentsManagerSupabase = ({ showToast, entrepriseId }) => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategorie, setFilterCategorie] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const initialFormData = {
    nom: "",
    categorie: "Politique",
    type: "PDF",
    taille: "",
    url: "",
    storage_path: "",
  };

  const [formData, setFormData] = useState(initialFormData);

  const categories = [
    "Politique",
    "Procédure",
    "Formulaire",
    "Contrat",
    "Rapport",
    "Formation",
    "Autre",
  ];

  const categorieColors = {
    Politique:
      "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
    Procédure:
      "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
    Formulaire:
      "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
    Contrat:
      "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
    Rapport:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
    Formation:
      "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400",
    Autre: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
  };

  const getFileIcon = (type) => {
    const t = (type || "").toLowerCase();
    if (t.includes("image") || t.includes("png") || t.includes("jpg"))
      return FileImage;
    if (t.includes("excel") || t.includes("xlsx") || t.includes("csv"))
      return FileSpreadsheet;
    if (t.includes("pdf")) return FileText;
    return File;
  };

  const formatSize = (bytes) => {
    if (!bytes) return "-";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  // Charger les documents
  useEffect(() => {
    if (entrepriseId) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [entrepriseId]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await loadDocumentsFromDB(entrepriseId);
      setDocuments(data);
    } catch (err) {
      setError("Erreur lors du chargement des documents.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Filtrage
  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch = (doc.nom || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategorie =
      filterCategorie === "all" || doc.categorie === filterCategorie;
    return matchesSearch && matchesCategorie;
  });

  // Stats par catégorie
  const stats = categories.reduce(
    (acc, cat) => {
      acc[cat] = documents.filter((d) => d.categorie === cat).length;
      return acc;
    },
    { total: documents.length }
  );

  const handleAdd = () => {
    setEditingDocument(null);
    setFormData(initialFormData);
    setShowModal(true);
  };

  const handleEdit = (document) => {
    setEditingDocument(document);
    setFormData({
      nom: document.nom || "",
      categorie: document.categorie || "Politique",
      type: document.type || "PDF",
      taille: document.taille || "",
      url: document.url || "",
      storage_path: document.storage_path || "",
    });
    setShowModal(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // Pour l'instant, on simule l'upload en stockant les métadonnées
      // En production, on utiliserait Supabase Storage
      const fileData = {
        nom: file.name,
        type: file.type || file.name.split(".").pop()?.toUpperCase() || "FILE",
        taille: file.size.toString(),
        url: URL.createObjectURL(file), // URL temporaire
        storage_path: `documents/${entrepriseId}/${Date.now()}_${file.name}`,
      };

      setFormData({
        ...formData,
        ...fileData,
      });

      showToast?.("Fichier prêt à être enregistré", "success");
    } catch (err) {
      showToast?.("Erreur lors du chargement du fichier.", "error");
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nom.trim()) {
      showToast?.("Le nom est obligatoire.", "error");
      return;
    }

    setIsSaving(true);
    try {
      if (editingDocument) {
        const updated = await updateDocumentInDB(editingDocument.id, formData);
        setDocuments(
          documents.map((d) => (d.id === editingDocument.id ? updated : d))
        );

        await logUpdate(entrepriseId, AUDIT_TYPES.DOCUMENT, formData.nom, {
          id: editingDocument.id,
          categorie: formData.categorie,
          type: formData.type,
        });

        showToast?.("Document modifié avec succès!", "success");
      } else {
        const newDocument = await addDocumentToDB({
          ...formData,
          entreprise_id: entrepriseId,
        });
        setDocuments([newDocument, ...documents]);

        await logCreate(entrepriseId, AUDIT_TYPES.DOCUMENT, formData.nom, {
          categorie: formData.categorie,
          type: formData.type,
          taille: formData.taille,
        });

        showToast?.("Document ajouté avec succès!", "success");
      }
      setShowModal(false);
    } catch (err) {
      showToast?.("Erreur lors de l'enregistrement.", "error");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const document = documents.find((d) => d.id === id);
    try {
      await deleteDocumentFromDB(id);
      setDocuments(documents.filter((d) => d.id !== id));

      await logDelete(
        entrepriseId,
        AUDIT_TYPES.DOCUMENT,
        document?.nom || "Document",
        {
          categorie: document?.categorie,
          type: document?.type,
        }
      );

      showToast?.("Document supprimé.", "success");
    } catch (err) {
      showToast?.("Erreur lors de la suppression.", "error");
      console.error(err);
    }
    setShowDeleteConfirm(null);
  };

  // États de chargement
  if (!entrepriseId) {
    return (
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 text-center">
        <AlertTriangle size={40} className="text-amber-500 mx-auto mb-4" />
        <p className="text-amber-700 dark:text-amber-300">
          Veuillez sélectionner une entreprise.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 size={40} className="text-sky-600 animate-spin mb-4" />
        <p className="text-gray-500 dark:text-gray-400">
          Chargement des documents...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <AlertTriangle size={40} className="text-red-500 mx-auto mb-4" />
        <p className="text-red-700 dark:text-red-300 font-medium mb-4">
          {error}
        </p>
        <button
          onClick={loadData}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 inline-flex items-center gap-2"
        >
          <RefreshCw size={16} /> Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
            Documents
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Politiques, procédures et documents de conformité
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadData}
            className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
          <button
            onClick={handleAdd}
            className="bg-sky-600 text-white px-4 py-2 rounded-lg hover:bg-sky-700 flex items-center gap-2 shadow-lg"
          >
            <Plus size={18} />
            <span>Ajouter</span>
          </button>
        </div>
      </div>

      {/* Stats rapides */}
      <div className="flex flex-wrap gap-3">
        <span className="px-3 py-1 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 rounded-full text-sm font-medium">
          {stats.total} documents
        </span>
        {categories.map(
          (cat) =>
            stats[cat] > 0 && (
              <span
                key={cat}
                className={`px-3 py-1 rounded-full text-sm font-medium ${categorieColors[cat]}`}
              >
                {stats[cat]} {cat}
              </span>
            )
        )}
      </div>

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search
              size={20}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Rechercher un document..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
            />
          </div>
          <select
            value={filterCategorie}
            onChange={(e) => setFilterCategorie(e.target.value)}
            className="px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
          >
            <option value="all">Toutes les catégories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
        {filteredDocuments.length === 0 ? (
          <div className="p-12 text-center">
            <FolderOpen
              size={48}
              className="text-gray-300 dark:text-gray-600 mx-auto mb-4"
            />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Aucun document
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {documents.length === 0
                ? "Commencez par ajouter vos documents de conformité."
                : "Aucun résultat ne correspond à vos filtres."}
            </p>
            {documents.length === 0 && (
              <button
                onClick={handleAdd}
                className="bg-sky-600 text-white px-6 py-3 rounded-lg hover:bg-sky-700 inline-flex items-center gap-2"
              >
                <Plus size={18} /> Ajouter un document
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredDocuments.map((doc) => {
              const FileIcon = getFileIcon(doc.type);
              return (
                <div
                  key={doc.id}
                  className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center text-white">
                      <FileIcon size={24} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {doc.nom}
                      </h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            categorieColors[doc.categorie] ||
                            categorieColors.Autre
                          }`}
                        >
                          {doc.categorie}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {doc.type}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatSize(doc.taille)}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(doc.created_at).toLocaleDateString("fr-CA")}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.url && (
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg"
                        title="Voir"
                      >
                        <Eye size={18} />
                      </a>
                    )}
                    <button
                      onClick={() => handleEdit(doc)}
                      className="p-2 text-sky-600 hover:bg-sky-100 dark:hover:bg-sky-900/30 rounded-lg"
                      title="Modifier"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(doc.id)}
                      className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg"
                      title="Supprimer"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-sky-600 to-indigo-600 text-white p-6 rounded-t-xl flex items-center justify-between sticky top-0">
              <h2 className="text-xl font-bold">
                {editingDocument ? "Modifier" : "Nouveau document"}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-white hover:bg-white/20 p-2 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Zone d'upload */}
              {!editingDocument && (
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    id="file-upload"
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    {isUploading ? (
                      <Loader2
                        size={40}
                        className="text-sky-500 animate-spin mx-auto mb-2"
                      />
                    ) : (
                      <Upload
                        size={40}
                        className="text-gray-400 mx-auto mb-2"
                      />
                    )}
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {formData.nom
                        ? `Fichier: ${formData.nom}`
                        : "Cliquez pour sélectionner un fichier"}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      PDF, Word, Excel, Images (max 10MB)
                    </p>
                  </label>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nom du document *
                </label>
                <input
                  type="text"
                  required
                  value={formData.nom}
                  onChange={(e) =>
                    setFormData({ ...formData, nom: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                  placeholder="Ex: Politique de confidentialité"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Catégorie
                  </label>
                  <select
                    value={formData.categorie}
                    onChange={(e) =>
                      setFormData({ ...formData, categorie: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                  >
                    {categories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Type
                  </label>
                  <input
                    type="text"
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                    placeholder="PDF, DOCX, etc."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  URL du document
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) =>
                    setFormData({ ...formData, url: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                  placeholder="https://..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-6 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isSaving && <Loader2 size={18} className="animate-spin" />}
                  {editingDocument ? "Enregistrer" : "Ajouter"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                <Trash2 className="text-red-600 dark:text-red-400" size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Supprimer ce document ?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Cette action est irréversible.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsManagerSupabase;
